import React, { useState } from 'react'
import moment from 'moment'
import { get, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@mui/styles'
import clsx from 'clsx'
import ListHeaderBackButton from './list-header-back-button'
import ColumnSettingModal from '@/modules/base/components/column-setting-modal/column-setting-modal'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import { DEBOUNCE_TIMES, SplitButton, SquareIconButton } from '@/modules/base/components/buttons/index'
import DocumentControlButton from '@/modules/base/components/buttons/document-control-button/document-control-button'
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import config from '@/config'
import lang from '@/lang'
import { useSelector, useDispatch } from 'react-redux'
import {
  updateSearchTerm,
  updateQuickFilter,
  toggleSettingModal,
  setShallExport,
  clearDateFilters,
  LIST_TYPES,
  PERIOD_FILTER_LISTS,
  updateFilterModel,
  updateAutoSizeColumns,
  getListSettingNameInBackend,
} from '@/redux/ducks/lists'
import { printSelection } from '@/redux/ducks/lists/helpers'
import DropDownItem from '@/modules/base/components/buttons/split-button/drop-down-item/drop-down-item'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import { useSearchParams } from 'react-router'
import request from '@/modules/base/util/request'
import toastList from '@/modules/base/components/toast/toastList'

const { datetimeFormat: { client: clientDateFormat } } = config
const excludedColumns = ['select', 'menu']

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '12px 30px 16px',
    backgroundColor: theme.listHeaderBackgroundColor,
    borderBottom: `1px solid ${theme.borderColor}`,
  },
  displayFlex: {
    display: 'flex',
    alignItems: 'center',
  },
  flexShrink: {
    flexShrink: 1,
    overflow: 'hidden',
    width: '500px',
  },
  header: {
    justifyContent: 'space-between',
  },
  titleWrapper: {
    marginRight: 15,
  },
  title: {
    fontSize: 24,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  count: {
    color: theme.textLight,
    fontSize: 16,
    marginLeft: 8,
  },
  button: {
    marginLeft: 10,
    marginRight: 0,
  },
  resetFilterButton: {
    '& svg': {
      '& .st0': {
        fill: theme.iconFillColor,
      },
      '& .st1': {
        fill: theme.isMeinbuero ? theme.iconFillColorLight : theme.iconFillColor,
      },
    },
  },
  settingsButton: {
    '& svg > *': {
      fill: `${theme.iconFillColor} !important`,
    },
  },
  content: {
    marginTop: 20,
  },
}))

const ListHeader = (props) => {
  const dispatch = useDispatch()
  const {
    title = '',
    subTitle,
    buttons = [],
    customButtonMarkup = null,
    backButtonUrl,
    hasResetFilterButton = true,
    hasSettingsButton = true,
    hasExportButton = true,
    hasPrintButton = false,
    exportSettings,
    children,
    gridApi,
    onFilterReset,
    onSettingSave,
    filterButtonDisabled = true,
    hideAutoSizeColumns = false,
    modelType = null,
    storePath = 'lists',
    topLine,
    bottomLine,
    containerClass,
    listSettingName,
    shortCountSentence = false,
    columns: columnsProps = null,
  } = props

  const [columns, setColumns] = useState(columnsProps)
  const classes = useStyles()
  const theme = useTheme()
  const [, setSearchParams] = useSearchParams()

  const listState = useSelector(state => get(state, storePath))
  const {
    searchTerm,
    filterModel,
    filter,
    filterItems,
    selectedNodes,
    settingModalOpen,
    isLoading,
    isSettingLoading,
    setting: { autoSizeColumns },
    meta: { count, totalCount },
    printing,
  } = listState[listSettingName]

  const isDisable = (isEmpty(filterModel) || !filterModel) && isEmpty(searchTerm) && filter === 'all'
  const selectCount = selectedNodes ? selectedNodes.length : 0

  const countContent = () => {
    if (totalCount && !shortCountSentence) {
      return `${count} ${lang.agGrid.of} ${totalCount} ${lang.agGrid.listed}${selectCount ? `, ${selectCount} ${lang.agGrid.selected}` : ''}`
    }
    return `${count}${selectCount ? `, ${selectCount} ${lang.agGrid.selected}` : ''}`
  }
  const countEl = subTitle ?? (!!totalCount && <div className={classes.count}>({countContent()})</div>)
  const backArrow = !!backButtonUrl && <ListHeaderBackButton url={backButtonUrl} />
  const customButtons = buttons.map(({
    label,
    icon,
    onClick,
    disabled,
    tooltip,
    tooltipHtml,
    customClass,
    className,
    type,
    variant,
    color,
    dataQsId = null,
    title = null,
    dropDownItems = [],
    secret = false,
    deepDisable = false,
  }, index) => {
    let button = null
    switch (type) {
      case 'SplitButton':
        button = (
          <SplitButton
            label={label}
            key={`list-header-btn-${index}`}
            dataQsId={dataQsId}
            onClick={onClick}
            disabled={disabled}
            deepDisable={deepDisable}
            debounceTime={DEBOUNCE_TIMES.OFF}
            className={clsx(classes.button, customClass, className)}
            autoClosePopper
            dataQsId={dataQsId}
          >
            {dropDownItems.map((item, i) => (
              <DropDownItem disabled={item.disabled} icon={item.icon} onClick={item.onClick} label={item.label} key={i} locked={item.locked} />
            ))}
          </SplitButton>
        )
        break
      case 'DocumentControlButton':
        button = (
          <DocumentControlButton
            key={`list-header-btn-${index}`}
            dataQsId={dataQsId}
            icon={icon}
            disabled={disabled}
            variant={variant || 'white'}
            onClick={onClick}
            title={title}
            customClass={clsx(classes.button, customClass, className)} />
        )
        break
      case 'SquareIconButton':
        button = (
          <SquareIconButton
            key={`list-header-btn-${index}`}
            icon={icon}
            size='large'
            iconColor='#fff'
            variant={variant || 'white'}
            tooltip={tooltip}
            disabled={disabled}
            customClass={clsx(classes.button, customClass, className)}
            onClick={onClick}
            dataQsId={dataQsId}
            secret={secret}
          />
        )
        break
      case 'ButtonIconSquare':
      default:
        button = (
          <ButtonIconSquare
            key={`list-header-btn-${index}`}
            label={label}
            icon={icon}
            onClick={onClick}
            disabled={disabled}
            customClass={clsx(classes.button, customClass, className)}
            variant={variant || 'fill'}
            color={color || 'blue'}
            dataQsId={dataQsId}
            secret={secret}
          />
        )
        break
    }
    if (tooltipHtml) {
      return (
        <RichTooltip key={`list-header-tooltip-${index}`} content={tooltipHtml} placement={'bottom'}>
          <span>
            {button}
          </span>
        </RichTooltip>
      )
    } else {
      return button
    }
  })

  const onResetFilter = () => {
    setSearchParams(new URLSearchParams())
    gridApi.setFilterModel(null)

    if (filterItems.length) {
      dispatch(updateQuickFilter(listSettingName, 'all'))
      if (listSettingName === LIST_TYPES.BANKING_LIST_TYPE || PERIOD_FILTER_LISTS.includes(listSettingName)) {
        dispatch(clearDateFilters(listSettingName))
      }
    }

    if (filterModel) {
      dispatch(updateFilterModel(listSettingName, null))
    }

    dispatch(updateSearchTerm(listSettingName, ''))
    onFilterReset?.()
  }

  const onOpenSettings = () => {
    dispatch(toggleSettingModal(listSettingName, true))

    if (gridApi) {
      setColumns(gridApi.getColumnDefs())
    }
  }

  const onSave = async(updatedColumns, autoSizeColumns) => {
    try {
      const data = updatedColumns
        .filter(column => !excludedColumns.includes(column.colId))
        .map((column) => {
          const { active, label, name, required, width } = column
          return { active, label, name, required, width }
        })

      // handle saving auto size columns here
      if (!hideAutoSizeColumns && autoSizeColumns !== props.autoSizeColumns) {
        const mappedListSettingName = getListSettingNameInBackend(listSettingName)
        await request(`${config.resourceHost}setting/userlist/${mappedListSettingName}`, {
          auth: true,
          method: 'PUT',
          data: { autoSizeColumns },
        })
        dispatch(updateAutoSizeColumns(listSettingName, autoSizeColumns))
      }

      onSettingSave && onSettingSave(data)
      autoSizeColumns && gridApi.sizeColumnsToFit()
    } catch (error) {
      console.error(error)
      toastList.showToast({
        message: lang.columnsSetting.saveError,
        type: 'error',
      })
    }
  }

  const defaultProcessCellCallBack = (params) => {
    const { processCellCallback } = exportSettings
    let result = params.value

    if (processCellCallback) {
      result = processCellCallback(params)
    }
    if (moment.isMoment(result)) {
      return moment(result).format(clientDateFormat)
    }
    return result
  }

  const onExport = () => {
    const { csvExportFilename } = exportSettings
    let allDisplayedColumns = gridApi.columnController?.displayedColumns
    if (!allDisplayedColumns) {
      allDisplayedColumns = gridApi.columnModel?.displayedColumns
    }
    const params = {
      fileName: `${csvExportFilename}_${moment().format('YYYY-MM-DD')}.xlsx`,
      sheetName: `${csvExportFilename}`,
      columnKeys: allDisplayedColumns?.filter((column) => !excludedColumns.includes(column.colId))
        .map((column) => column.colId),
    }
    // if we have selected rows, then ag-grid has already all the data also for server side and we can go the "old way"
    const selectedRowCount = gridApi.getSelectedNodes().length
    if (gridApi.rowModel.constructor.name === 'ServerSideRowModel' && selectedRowCount === 0) {
      const { sortModel, filterModel, ...restParams } = gridApi.serverSideRowModel.getParams()
      gridApi.serverSideRowModel.datasource.getRows({
        ...restParams,
        request: { startRow: 0, endRow: 0, sortModel, filterModel },
        successCallback: (exportItems) => {
          params.processCellCallback = defaultProcessCellCallBack
          dispatch(setShallExport(listSettingName, true, exportItems, params))
        },
        failCallback: () => {
          toastList.showToast({
            message: lang.listHeader.errors.export,
            type: 'error',
          })
        },
      })
    } else {
      try {
        params.processCellCallback = defaultProcessCellCallBack
        params.onlySelectedAllPages = selectCount > 0
        gridApi.exportDataAsExcel(params)
      } catch (error) {
        toastList.showToast({
          message: lang.listHeader.errors.export,
          type: 'error',
        })
      }
    }
  }

  const onPrint = () => {
    printSelection(listSettingName, modelType, selectedNodes)
  }

  const resetFilterButton = hasResetFilterButton && (
    <DocumentControlButton
      width='15px'
      height='15px'
      icon={FilterAltOffRoundedIcon}
      dataQsId={`${listSettingName}-listHeader-resetFilter`}
      onClick={onResetFilter}
      customClass={`${classes.button} ${classes.resetFilterButton}`}
      disabled={filterButtonDisabled && isDisable}
      title={lang.resetFilter}
    />
  )

  const settingsButton = hasSettingsButton && (
    <DocumentControlButton
      width='15px'
      height='15px'
      dataQsId={`${listSettingName}-listHeader-settings`}
      icon={SettingsRoundedIcon}
      onClick={onOpenSettings}
      customClass={`${classes.button} ${classes.settingsButton}`}
      title={lang.changeColumns}
    />
  )

  const exportButton = hasExportButton && (
    <DocumentControlButton
      width='15px'
      height='15px'
      icon={DownloadRoundedIcon}
      dataQsId={`${listSettingName}-listHeader-export`}
      iconColor={theme.iconFillColor}
      onClick={onExport}
      customClass={classes.button}
      title={lang.export}
      disabled={totalCount < 1}
    />
  )

  const printButton = hasPrintButton && (
    <DocumentControlButton
      width='15px'
      height='15px'
      icon={PrintRoundedIcon}
      dataQsId={`${listSettingName}-listHeader-print`}
      onClick={onPrint}
      customClass={`${classes.button} ${classes.printButton}`}
      title={lang.generics.listHeaderPrintButton(selectedNodes)}
      disabled={!selectedNodes.length || printing}
    />
  )

  return (
    <>
      <div className={clsx(classes.container, containerClass)} data-qs-id={`list-header-${listSettingName}`}>
        <div className={clsx(classes.displayFlex, classes.header)}>
          <div className={clsx(classes.displayFlex, classes.flexShrink)}>
            {backArrow}
            <div className={clsx(classes.displayFlex, classes.flexShrink, classes.titleWrapper)}>
              <div className={classes.title}>{title}</div>
              {!isLoading && countEl}
            </div>
          </div>
          {!topLine && <div className={classes.displayFlex}>
            {resetFilterButton}
            {settingsButton}
            {exportButton}
            {printButton}
            {customButtons}
            {customButtonMarkup}
          </div>}
          {!!topLine && <div className={classes.displayFlex}>
            {topLine}
          </div>
          }
        </div>
        {!!children && <div className={classes.content}>
          {children}
        </div>}
        {!!bottomLine && <div className={classes.content}>
          {bottomLine}
        </div>}
      </div>
      {(hasSettingsButton && settingModalOpen) && (
        <ColumnSettingModal
          open={settingModalOpen}
          isLoading={isSettingLoading}
          updatedColumns={columns}
          autoSizeColumns={autoSizeColumns}
          hideAutoSizeColumns={hideAutoSizeColumns}
          onCancel={() => dispatch(toggleSettingModal(listSettingName, false))}
          onSave={onSave}
        />
      )}
    </>
  )
}

ListHeader.propTypes = {
  containerClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  backButtonUrl: PropTypes.string,
  buttons: PropTypes.array,
  count: PropTypes.number,
  hasResetFilterButton: PropTypes.bool,
  hasSettingsButton: PropTypes.bool,
  hasExportButton: PropTypes.bool,
  hasPrintButton: PropTypes.bool,
  onFilterReset: PropTypes.func,
  onSettingSave: PropTypes.func,
  filterButtonDisabled: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  totalCount: PropTypes.number,
  selectCount: PropTypes.number,
  modelType: PropTypes.string,
  columns: PropTypes.array,
  children: PropTypes.node,
  gridApi: PropTypes.object,
  settingLoading: PropTypes.bool,
  exportSettings: PropTypes.object,
  listSettingName: PropTypes.string,
  shortCountSentence: PropTypes.bool,
  customButtonMarkup: PropTypes.node,
}

export default ListHeader
